import { Dispatch, SetStateAction, useEffect } from 'react';
import { useFormik } from 'formik';
import { isEqual, merge } from 'lodash-es';

import { ICalendarEvent, IPositionedEvent } from '~/Screens/Calendar/interfaces';
import { useValidateEventCreation } from './useValidateEventCreation';
import { EventCreationStep } from '~/Screens/Calendar/CalendarView/components/CreatingEvent/types';
import { useCreateNewCalendarEvent } from './useCreateNewCalendarEvent';

interface IUseCreatingEventForm {
  event: ICalendarEvent;
  setNewEvent: Dispatch<SetStateAction<null | IPositionedEvent>>;
  setCreationStep: Dispatch<SetStateAction<EventCreationStep>>;
}

export const useEventCreationForm = ({
  event,
  setNewEvent,
  setCreationStep,
}: IUseCreatingEventForm) => {
  const { initialValues, validationSchema } = useValidateEventCreation({ event });
  const { eventAction } = useCreateNewCalendarEvent({
    onSettled: () => setCreationStep(EventCreationStep.CREATED),
  });
  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (event: ICalendarEvent) => {
      setCreationStep(EventCreationStep.SUBMITTING);
      eventAction.mutate(event);
      setNewEvent((prev) => prev && { ...prev, event, isLoading: true });
    },
  });
  const { name, end, start } = formik.values;

  useEffect(() => {
    const newEvent = merge({}, event, { name, end, start });
    if (isEqual(event, newEvent)) {
      return;
    }
    setNewEvent((prev) => prev && { ...prev, event: newEvent });
  }, [name, end, start, event]);

  return { formik };
};
